import React, { useState, useEffect } from 'react';
import Header from './Header';
import Footer from './Footer';
import { CourseService, Course } from '../services/supabase';
import CourseMap from './CourseMap';

// Error Boundary Component
class ErrorBoundary extends React.Component<
  { children: React.ReactNode },
  { hasError: boolean; error?: Error }
> {
  constructor(props: { children: React.ReactNode }) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('ErrorBoundary caught an error:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen bg-white">
          <Header />
          <main className="pt-16">
            <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-20">
              <div className="text-center">
                <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg className="w-8 h-8 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">Something went wrong</h3>
                <p className="text-gray-600 mb-4">An error occurred while loading the page.</p>
                <button 
                  onClick={() => window.location.reload()} 
                  className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
                >
                  Refresh Page
                </button>
              </div>
            </div>
          </main>
          <Footer />
        </div>
      );
    }

    return this.props.children;
  }
}

const CoursesPage: React.FC = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [courses, setCourses] = useState<Course[]>([]);
  const [filteredCourses, setFilteredCourses] = useState<Course[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [userLocation, setUserLocation] = useState<{lat: number, lng: number} | null>(null);
  const [locationPermission, setLocationPermission] = useState<'granted' | 'denied' | 'prompt'>('prompt');
  const [isProcessingLocation, setIsProcessingLocation] = useState(false);
  const [coursesToShow, setCoursesToShow] = useState(6);

  // Calculate distance between two coordinates (in miles)
  const calculateDistance = (lat1: number, lng1: number, lat2: number, lng2: number): number => {
    const R = 3959; // Earth's radius in miles
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLng = (lng2 - lng1) * Math.PI / 180;
    const a = Math.sin(dLat/2) * Math.sin(dLat/2) +
              Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
              Math.sin(dLng/2) * Math.sin(dLng/2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
    return R * c;
  };

  // Request user location
  const requestLocation = async () => {
    console.log('requestLocation called');
    console.log('Browser:', navigator.userAgent);
    
    if (!navigator.geolocation) {
      console.log('Geolocation not supported by this browser');
      setLocationPermission('denied');
      return;
    }

    setIsProcessingLocation(true);
    console.log('Starting location request...');
    
    try {
      const position = await new Promise<GeolocationPosition>((resolve, reject) => {
        console.log('Calling getCurrentPosition...');
        
        // Safari-specific options
        const options = {
          enableHighAccuracy: true,
          timeout: 20000, // Increased timeout for Safari
          maximumAge: 0 // Don't use cached location in Safari
        };
        
        navigator.geolocation.getCurrentPosition(
          (pos) => {
            console.log('Location success:', pos);
            resolve(pos);
          },
          (err) => {
            console.log('Location error:', err);
            console.log('Error code:', err.code);
            console.log('Error message:', err.message);
            reject(err);
          },
          options
        );
      });

      console.log('Position received:', position);
      const { latitude, longitude } = position.coords;
      console.log('Location coordinates:', latitude, longitude);
      
      // Validate coordinates
      if (isNaN(latitude) || isNaN(longitude)) {
        throw new Error('Invalid coordinates received');
      }
      
      console.log('Setting user location...');
      setUserLocation({ lat: latitude, lng: longitude });
      console.log('Setting location permission to granted...');
      setLocationPermission('granted');
      console.log('Location processing complete');
      
    } catch (error) {
      console.error('Location access error:', error);
      setLocationPermission('denied');
    } finally {
      console.log('Setting processing location to false...');
      setIsProcessingLocation(false);
    }
  };

  // Load more courses
  const loadMoreCourses = () => {
    setCoursesToShow(prev => prev + 6);
  };

  // Load courses from database
  useEffect(() => {
    const loadCourses = async () => {
      try {
        setLoading(true);
        console.log('Loading courses from database...');
        const courseData = await CourseService.getCourses();
        console.log('Courses loaded:', courseData.length);
        
        // Sort by distance if user location is available
        if (userLocation) {
          console.log('Sorting courses by distance from user location:', userLocation);
          const coursesWithDistance = courseData.map(course => ({
            ...course,
            distance: calculateDistance(
              userLocation.lat, 
              userLocation.lng, 
              course.latitude, 
              course.longitude
            )
          }));
          
          coursesWithDistance.sort((a, b) => a.distance - b.distance);
          console.log('Courses sorted by distance');
          
          // Update state in a single batch to prevent DOM issues
          setCourses([...coursesWithDistance]);
          setFilteredCourses([...coursesWithDistance]);
        } else {
          console.log('No user location, using default course order');
          setCourses([...courseData]);
          setFilteredCourses([...courseData]);
        }
      } catch (err) {
        console.error('Error loading courses:', err);
        const errorMessage = err instanceof Error ? err.message : 'Unknown error';
        console.error('Full error:', err);
        
        // Provide more helpful error message
        if (errorMessage.includes('JWT') || errorMessage.includes('Invalid API key')) {
          setError('Database configuration error. Please check your Supabase credentials.');
        } else if (errorMessage.includes('Failed to fetch') || errorMessage.includes('Network')) {
          setError('Network error. Please check your internet connection and try again.');
        } else {
          setError(`Failed to load courses: ${errorMessage}. Please check the browser console for more details.`);
        }
      } finally {
        setLoading(false);
      }
    };

    loadCourses();
  }, [userLocation]);

  // Request location on component mount
  useEffect(() => {
    requestLocation();
  }, []);

  // Filter courses based on search query
  useEffect(() => {
    if (!searchQuery.trim()) {
      setFilteredCourses(courses);
    } else {
      const filtered = courses.filter(course =>
        course.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        course.location.toLowerCase().includes(searchQuery.toLowerCase())
      );
      setFilteredCourses(filtered);
    }
  }, [searchQuery, courses]);

  // Add error boundary to prevent white screen
  if (error && !loading) {
    return (
      <div className="min-h-screen bg-white">
        <Header />
        <main className="pt-16">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-20">
            <div className="text-center">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Something went wrong</h3>
              <p className="text-gray-600 mb-4">{error}</p>
              <button 
                onClick={() => window.location.reload()} 
                className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
              >
                Refresh Page
              </button>
            </div>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* Main Content */}
      <main className="pt-16">
        {/* Hero Section */}
        <section className="py-20 bg-white">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h1 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
                Hole In 1 Courses
              </h1>
              <div className="inline-flex items-center px-4 py-2 rounded-full bg-gray-100 text-gray-700 text-sm font-medium">
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
                More courses coming soon
              </div>
            </div>

            {/* Location Permission Banner */}
            {locationPermission === 'prompt' && !isProcessingLocation && (
              <div className="mb-6 bg-blue-50 border border-blue-200 rounded-lg p-4 max-w-2xl mx-auto">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <svg className="h-5 w-5 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <h3 className="text-sm font-medium text-blue-800">
                      Show courses near you
                    </h3>
                    <p className="text-sm text-blue-700 mt-1">
                      Allow location access to see the closest golf courses with prizes. Your location is not stored or shared.
                      {navigator.userAgent.includes('Safari') && !navigator.userAgent.includes('Chrome') && (
                        <span className="block mt-1 font-medium">
                          Safari users: Click "Allow" when prompted, or enable location services in Safari preferences.
                        </span>
                      )}
                    </p>
                    <button
                      onClick={requestLocation}
                      className="mt-2 text-sm font-medium text-blue-600 hover:text-blue-500 underline"
                    >
                      Allow Location Access →
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Location Denied Banner */}
            {locationPermission === 'denied' && !isProcessingLocation && (
              <div className="mb-6 bg-gray-50 border border-gray-200 rounded-lg p-4 max-w-2xl mx-auto">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <h3 className="text-sm font-medium text-gray-800">
                      Location access denied
                    </h3>
                    <p className="text-sm text-gray-600 mt-1">
                      Courses are shown in default order. You can enable location access in your browser settings to see nearby courses.
                    </p>
                    <button
                      onClick={async () => {
                        setLocationPermission('prompt');
                        setIsProcessingLocation(true);
                        try {
                          await requestLocation();
                        } catch (err) {
                          console.error('Error requesting location:', err);
                        } finally {
                          setIsProcessingLocation(false);
                        }
                      }}
                      className="mt-2 text-sm font-medium text-gray-600 hover:text-gray-500 underline"
                    >
                      Try Again →
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Location Processing Banner */}
            {isProcessingLocation && (
              <div className="mb-6 bg-green-50 border border-green-200 rounded-lg p-4 max-w-2xl mx-auto">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <svg className="animate-spin h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" style={{ color: '#9CB75A' }}>
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <h3 className="text-sm font-medium" style={{ color: '#9CB75A' }}>
                      Finding courses near you...
                    </h3>
                    <p className="text-sm mt-1" style={{ color: '#9CB75A' }}>
                      Sorting courses by distance from your location.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Search Bar */}
            <div className="max-w-2xl mx-auto mb-12">
              <div className="relative">
                <input
                  type="text"
                  placeholder="Search for courses by name or location..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full px-6 py-4 pl-12 text-lg border-2 border-gray-200 rounded-2xl focus:outline-none transition-colors"
                  style={{ '--tw-ring-color': '#9CB75A' } as React.CSSProperties}
                  onFocus={(e) => e.target.style.borderColor = '#9CB75A'}
                  onBlur={(e) => e.target.style.borderColor = '#d1d5db'}
                />
                <div className="absolute left-4 top-1/2 transform -translate-y-1/2">
                  <svg className="w-6 h-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                </div>
              </div>
            </div>

            {/* Interactive Google Map */}
            <div className="mb-12">
              {loading ? (
                <div className="rounded-2xl h-[500px] w-full bg-gray-100 flex items-center justify-center">
                  <div className="text-center">
                    <div className="w-16 h-16 bg-gray-300 rounded-full flex items-center justify-center mx-auto mb-4">
                      <svg className="w-8 h-8 text-gray-500 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                      </svg>
                    </div>
                    <p className="text-gray-500 text-lg">Loading map...</p>
                  </div>
                </div>
              ) : (
                <CourseMap courses={filteredCourses} userLocation={userLocation} />
              )}
            </div>

            {/* Course List */}
            {loading ? (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {[1, 2, 3, 4, 5, 6].map((i) => (
                  <div key={i} className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100 animate-pulse">
                    <div className="h-6 bg-gray-200 rounded mb-4"></div>
                    <div className="h-4 bg-gray-200 rounded mb-2"></div>
                    <div className="h-4 bg-gray-200 rounded mb-4"></div>
                    <div className="h-8 bg-gray-200 rounded"></div>
                  </div>
                ))}
              </div>
            ) : error ? (
              <div className="text-center py-12">
                <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg className="w-8 h-8 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">Unable to load courses</h3>
                <p className="text-gray-600">{error}</p>
              </div>
            ) : (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {filteredCourses.slice(0, coursesToShow).map((course, index) => (
                  <div
                    key={`${course.id}-${index}`}
                    className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100 hover:shadow-xl transition-all duration-300 cursor-pointer"
                    onClick={() => window.location.href = `/course/${course.id}`}
                  >
                    <div className="mb-4">
                      <h3 className="text-xl font-bold text-gray-900 mb-2">{course.name}</h3>
                      <p className="text-gray-600 text-sm">
                        {course.location}
                        {(course as any).distance && (
                          <span className="ml-2 text-black font-medium">
                            • {(course as any).distance.toFixed(1)} miles away
                          </span>
                        )}
                      </p>
                    </div>
                    
                    {/* Par 3 Holes */}
                    <div className="space-y-2">
                      <div className="flex flex-wrap gap-2">
                        {course.holes && course.holes
                          .sort((a, b) => a.hole_number - b.hole_number)
                          .map((hole, index) => (
                          <div
                            key={index}
                            className="w-8 h-8 rounded-full flex items-center justify-center text-white text-sm font-medium"
                            style={{ backgroundColor: '#9CB75A' }}
                          >
                            #{hole.hole_number}
                          </div>
                        ))}
                      </div>
                      <p className="text-xs text-gray-500">
                        {course.holes?.length || 0} par 3 holes with prizes
                      </p>
                    </div>
                    
                  </div>
                ))}
              </div>
            )}

            {/* Load More Button */}
            {filteredCourses.length > coursesToShow && (
              <div className="text-center mt-12">
                <button
                  onClick={loadMoreCourses}
                  className="px-8 py-3 bg-white border-2 border-gray-300 rounded-lg text-gray-700 font-medium hover:border-gray-400 hover:bg-gray-50 transition-colors"
                >
                  Load More Courses ({filteredCourses.length - coursesToShow} remaining)
                </button>
              </div>
            )}

            {/* No Results */}
            {filteredCourses.length === 0 && searchQuery && (
              <div className="text-center py-12">
                <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.172 16.172a4 4 0 015.656 0M9 12h6m-6-4h6m2 5.291A7.962 7.962 0 0112 15c-2.34 0-4.29-1.009-5.824-2.709M15 6.291A7.962 7.962 0 0012 5c-2.34 0-4.29 1.009-5.824 2.709" />
                  </svg>
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">No courses found</h3>
                <p className="text-gray-600">Try searching with different keywords or check back later as we add more courses.</p>
              </div>
            )}

          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

// Wrap the component with ErrorBoundary
const CoursesPageWithErrorBoundary: React.FC = () => {
  return (
    <ErrorBoundary>
      <CoursesPage />
    </ErrorBoundary>
  );
};

export default CoursesPageWithErrorBoundary;
